import { ReactNode, useEffect, useId, useRef } from 'react'
import { X } from 'lucide-react'

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  /** Grafy a široké náhľady. */
  '2xl': 'max-w-5xl',
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

// Pokrýva aj najdlhší čas dvojkliku, aký OS dovolí nastaviť (Windows ~900 ms). Dlhšie okno
// nevadí: druhý klik dvojkliku spozná `detail ≥ 2`, samostatný klik má `detail` 1.
const FOLLOW_UP_CLICK_MS = 1000

// Okno sa otvára aj zatvára prvým klikom dvojkliku a druhý klik by dopadol inam: po otvorení na
// pozadie (okno hneď zavrie) alebo na tlačidlo potvrdenia, po zatvorení na obsah pod ním (riadok
// tabuľky otvorí iný záznam). Zachytávanie na window ho zastaví skôr, než ho uvidí React. Klik
// z klávesnice (Enter, medzerník) a zo skriptu má `detail` 0, ten prejde vždy.
function ignoreFollowUpClicks() {
  const swallow = (e: MouseEvent) => {
    if (e.detail < 2) return
    e.stopPropagation()
    e.preventDefault()
  }
  window.addEventListener('click', swallow, true)
  window.addEventListener('dblclick', swallow, true)
  window.setTimeout(() => {
    window.removeEventListener('click', swallow, true)
    window.removeEventListener('dblclick', swallow, true)
  }, FOLLOW_UP_CLICK_MS)
}

interface ModalProps {
  /** Optional header title. When omitted, no header row is rendered. */
  title?: ReactNode
  /** Called on close button, Esc key, or backdrop click. */
  onClose: () => void
  /** Panel max-width. Defaults to `md`. */
  size?: ModalSize
  /** Body content. Callers keep their own padding (e.g. `p-5 space-y-4`). */
  children: ReactNode
  /** Extra classes on the white panel. */
  className?: string
  /** Close when the dark backdrop is clicked. Defaults to true. */
  closeOnBackdrop?: boolean
  /** Close when Escape is pressed. Defaults to true. */
  closeOnEsc?: boolean
  /** Accessible name when `title` is not rendered (no visible header). */
  ariaLabel?: string
}

/**
 * Shared modal overlay. Extracts the byte-identical
 * `fixed inset-0 … / bg-white rounded-xl shadow-xl …` skeleton that was
 * hand-rolled across the admin app, and adds Esc / backdrop-click close.
 *
 * Accessibility: `role="dialog"` + `aria-modal`, focus moves to the panel on
 * open and is restored on close, Tab/Shift+Tab cycle within the dialog, and
 * body scroll is locked while open. The Tab trap only reacts to keydown (never
 * focus events), so portal-rendered dropdowns (react-select & co.) that live
 * outside the panel DOM keep working.
 *
 * The body is caller-owned — pass your existing `<form>`/`<div>` as children
 * with its own padding so migration is a 1:1 structural swap.
 *
 * Height: the panel is capped at the viewport (`max-h-full` inside the padded
 * `fixed inset-0` overlay) and scrolls internally. Body scroll is locked while
 * the modal is open, so without this cap a form taller than a short viewport
 * (laptop 768p, browser zoom, OS scaling) would be centred with its header and
 * footer buttons pushed outside the screen and unreachable. Callers that want a
 * pinned header/footer can still pass `flex flex-col` and scroll an inner area.
 */
export function Modal({
  title,
  onClose,
  size = 'md',
  children,
  className = '',
  closeOnBackdrop = true,
  closeOnEsc = true,
  ariaLabel,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    if (!closeOnEsc) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeOnEsc, onClose])

  // Focus management: focus the panel on open, restore the trigger on close.
  // Body scroll lock for the modal's lifetime.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    // Respect children that focus themselves on mount (autoFocus inputs —
    // React applies those during commit, before this effect runs).
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus()
    }

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus?.()
    }
  }, [])

  useEffect(() => {
    ignoreFollowUpClicks()
    return ignoreFollowUpClicks
  }, [])

  // Focus trap — Tab/Shift+Tab cycle inside the panel. Keydown-only, so
  // portal-based dropdowns rendered outside the panel are never disturbed.
  const onTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const panel = panelRef.current
    if (!panel) return
    // If focus escaped the panel (e.g. a portal dropdown), don't interfere.
    if (!panel.contains(document.activeElement)) return

    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement)

    if (focusables.length === 0) {
      e.preventDefault()
      panel.focus()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (e.shiftKey) {
      if (active === first || active === panel) {
        e.preventDefault()
        last.focus()
      }
    } else if (active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        aria-label={title == null ? ariaLabel : undefined}
        tabIndex={-1}
        onKeyDown={onTrapKeyDown}
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-h-full overflow-y-auto outline-none ${SIZE_CLASS[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title != null && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 id={titleId} className="font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            <button type="button" onClick={onClose} className="btn-ghost p-1.5 h-auto">
              <X size={16} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
